import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Sparkle, Check, Crown } from "@phosphor-icons/react";

export default function AISalesPlans() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");

  const { data: plansData } = useQuery({ queryKey: ["ai-plans"], queryFn: async () => (await api.get("/ai-sales/plans")).data });
  const { data: subData } = useQuery({ queryKey: ["ai-subscription"], queryFn: async () => (await api.get("/ai-sales/subscription")).data });
  const plans = plansData?.plans || [];
  const current = subData?.subscription?.plan_key;

  const subscribe = useMutation({
    mutationFn: async (plan_key) => (await api.post("/ai-sales/subscribe", { plan_key, cycle })).data,
    onSuccess: () => { toast.success("Plan activated — your iYeneh Assistant is ready."); qc.invalidateQueries({ queryKey: ["ai-subscription"] }); qc.invalidateQueries({ queryKey: ["me"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const priceFor = (p) => (cycle === "annual" ? p.price_cents * 10 : p.price_cents);

  return (
    <div className="mx-auto max-w-6xl space-y-8" data-testid="ai-sales-plans">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Sparkle size={26} weight="fill" /></div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">iYeneh SI Sales</h1>
        <p className="mt-2 text-muted-foreground">Your 24/7 SI Sales Team — choose the plan that fits your business.</p>
        <div className="mt-5 inline-flex rounded-full border border-border bg-card p-1 text-sm">
          {["monthly", "annual"].map((c) => (
            <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
              className={`rounded-full px-4 py-1.5 font-medium capitalize transition-colors ${cycle === c ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {c}{c === "annual" && <span className="ml-1 text-xs opacity-80">(2 months free)</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3 xl:grid-cols-5">
        {plans.map((p) => {
          const isCustom = !p.price_cents;
          const isCurrent = current === p.key;
          return (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`flex flex-col rounded-3xl border bg-card p-5 ${p.recommended ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
              {p.badge && <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-semibold text-primary"><Crown size={12} weight="fill" /> {p.badge}</span>}
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <p className="mt-1 min-h-[2.5rem] text-xs text-muted-foreground">{p.tagline}</p>
              <div className="mt-3">
                {isCustom ? <span className="font-display text-2xl font-semibold">Custom</span>
                  : <><span className="font-display text-3xl font-semibold">{money(priceFor(p))}</span><span className="text-sm text-muted-foreground">/{cycle === "annual" ? "yr" : "mo"}</span></>}
              </div>
              <ul className="mt-4 flex-1 space-y-2">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><Check size={16} weight="bold" className="mt-0.5 shrink-0 text-primary" /><span>{f}</span></li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled data-testid={`plan-current-${p.key}`} className="mt-5 rounded-full border border-primary bg-primary/10 py-2.5 text-sm font-semibold text-primary">Current plan</button>
              ) : isCustom ? (
                <button data-testid={`plan-contact-${p.key}`} onClick={() => navigate("/app/ai-sales")}
                  className="mt-5 rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary">{p.cta || "Contact Sales"}</button>
              ) : (
                <button data-testid={`plan-subscribe-${p.key}`} disabled={subscribe.isPending} onClick={() => subscribe.mutate(p.key)}
                  className="mt-5 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
                  {subscribe.isPending ? "Activating…" : "Get started"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-muted-foreground">{plansData?.contact_note}</p>
    </div>
  );
}
